import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { useContext } from "react";
import { FixedPhoneNumber } from "shared/util/validate";

export const useBackend = () => {
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const getLocationFromAddress = useCallback(
    async (address) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/locations/get-location`,
          "POST",
          JSON.stringify({ address })
        );

        return responseData;
      } catch {}
      return null;
    },
    [sendRequest]
  );

  const getAddressFromLocation = useCallback(
    async (location) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/locations/get-address`,
          "POST",
          JSON.stringify({ location })
        );

        return responseData;
      } catch {}
      return null;
    },
    [sendRequest]
  );

  const getDirectionSingle = useCallback(
    async (origin, destination) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/directions/getDirectionSingle`,
          "POST",
          JSON.stringify({ origin, destination })
        );
        return responseData?.directionSingle || {};
      } catch {}
      return null;
    },
    [sendRequest]
  );

  const getDirectionsWithWaypoints = useCallback(
    async (origin, destination, waypoints) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/directions/getDirectionsWithWaypoints`,
          "POST",
          JSON.stringify({ origin, destination, waypoints })
        );
        return responseData?.directionsWithWaypoints || [];
      } catch {}
      return null;
    },
    [sendRequest]
  );

  // Google Route Optimization API tabanlı servis rotası optimizasyonu.
  // origin: okul { location:{lat,lng}, id? }; stops: [{ location:{lat,lng}, id }]
  // isPickup: toplama mı (true) dağıtma mı (false); anchorTime: okul saati "HH:mm" (opsiyonel).
  // Dönüş: { orderedStops:[{id, transferTime}], skippedStopIds, totalDistance, totalDuration } veya hata/iptalde null.
  const optimizeServiceRoute = useCallback(
    async (origin, stops, isPickup, anchorTime) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/route-optimization/optimize-service-route`,
          "POST",
          JSON.stringify({ origin, stops, isPickup, anchorTime })
        );
        return responseData?.routeOptimization || null;
      } catch {}
      return null;
    },
    [sendRequest]
  );
  const sendSMS = async (phonenumber, message, userId = null, studentId = null, vehicleRouteId = null) => {
    const APIEndPoint = "xrytutdvsdfds";

    const phone = FixedPhoneNumber(phonenumber);
    const text = message.trim();
    if (!phone || !text) {
      window.toast.error(t("actionFailed"));
      return;
    }

    const smsData = {
      user: userId || undefined,
      student: studentId || undefined,
      vehicleRoute: vehicleRouteId || undefined,
      phone,
      text,
    };

    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/${APIEndPoint}`, "POST", JSON.stringify(smsData), {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      });

      window.toast.success(t("actionSuccess"));
    } catch (err) {
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  const getSMSByVehicleRoute = useCallback(
    async (vehicleRouteId) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/xrytutdvsdfds/${vehicleRouteId}`,
          "POST"
        );
        return responseData?.smses || [];
      } catch (err) {
        return [];
      }
    },
    [sendRequest]
  );

  const checkUsageAllowance = useCallback(
    async (usageType, units = 1) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/usage/check-allowance`,
          "POST",
          JSON.stringify({ usageType, units })
        );
        return responseData || { allowed: true, remaining: Infinity, quota: Infinity, used: 0, requested: units };
      } catch {
        // If the check fails, allow the operation to proceed (fail-open)
        return { allowed: true, remaining: Infinity, quota: Infinity, used: 0, requested: units };
      }
    },
    [sendRequest]
  );

  const getRemainingUsage = useCallback(
    async (usageType) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/usage/remaining/${usageType}`,
          "GET"
        );
        return responseData || { allowed: true, remaining: Infinity, quota: Infinity, used: 0 };
      } catch {
        return { allowed: true, remaining: Infinity, quota: Infinity, used: 0 };
      }
    },
    [sendRequest]
  );

  return {
    getLocationFromAddress,
    getAddressFromLocation,
    sendSMS,
    getSMSByVehicleRoute,
    getDirectionSingle,
    getDirectionsWithWaypoints,
    optimizeServiceRoute,
    checkUsageAllowance,
    getRemainingUsage,
  };
};
